/**
 * AttendanceRegularization — Employee submits + HR approves
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Button, Badge, Table,
    Form, Modal, Spinner, Alert,
} from 'react-bootstrap';
import { Clock, Plus, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const HR_ROLES  = ['super-admin', 'admin', 'hr-manager', 'account-manager', 'reporting-manager'];
const MAX_PAST_DAYS = 30;

const STATUS_BADGE = {
    pending:  { bg: 'warning', text: 'dark',  label: 'Pending'  },
    approved: { bg: 'success', text: 'white', label: 'Approved' },
    rejected: { bg: 'danger',  text: 'white', label: 'Rejected' },
};

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const toMins = (t) => {
    if (!t) return null;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
};

const validateForm = (form) => {
    const e = {};
    const today     = new Date(); today.setHours(23, 59, 59, 999);
    const pastLimit = new Date(); pastLimit.setDate(pastLimit.getDate() - MAX_PAST_DAYS);
    pastLimit.setHours(0, 0, 0, 0);

    if (!form.date) {
        e.date = 'Date is required';
    } else {
        const d = new Date(form.date);
        if (d > today)          e.date = 'Date cannot be in the future';
        else if (d < pastLimit) e.date = `Cannot regularize attendance older than ${MAX_PAST_DAYS} days`;
    }

    if (!form.requested_check_in && !form.requested_check_out)
        e.times = 'Enter at least one correct time (check-in or check-out)';

    if (form.requested_check_in && form.requested_check_out) {
        if (toMins(form.requested_check_out) <= toMins(form.requested_check_in))
            e.times = 'Check-out time must be after check-in time';
    }

    const reason = form.reason?.trim();
    if (!reason)                e.reason = 'Reason is required';
    else if (reason.length < 10) e.reason = 'Please provide a more detailed reason (min 10 characters)';
    else if (reason.length > 500) e.reason = 'Reason cannot exceed 500 characters';

    return e;
};

const AttendanceRegularization = () => {
    const { user } = useAuth();
    const isHR     = HR_ROLES.includes(user?.role);
    const [tab, setTab] = useState(isHR ? 'pending' : 'my');

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <Clock size={22} className="text-primary"/>Attendance Regularization
                    </h4>
                    <p className="text-muted small mb-0">
                        Request correction for missed or wrong check-in / check-out
                    </p>
                </div>
            </div>
            <div className="d-flex gap-1 mb-4 flex-wrap">
                <Button size="sm" variant={tab==='my'?'primary':'outline-secondary'} onClick={()=>setTab('my')}>My Requests</Button>
                {isHR && <>
                    <Button size="sm" variant={tab==='pending'?'primary':'outline-secondary'} onClick={()=>setTab('pending')}>Pending Approval</Button>
                    <Button size="sm" variant={tab==='history'?'primary':'outline-secondary'} onClick={()=>setTab('history')}>History</Button>
                </>}
            </div>
            {tab==='my'      && <MyRequestsTab/>}
            {tab==='pending' && isHR && <PendingTab/>}
            {tab==='history' && isHR && <PendingTab status="approved"/>}
        </div>
    );
};

const MyRequestsTab = () => {
    const [requests, setRequests] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [showForm, setShowForm] = useState(false);
    const load = () => {
        setLoading(true);
        axiosInstance.get('/attendance/regularize/my')
            .then(r => setRequests(r.data.data || []))
            .catch(()=>{}).finally(()=>setLoading(false));
    };
    useEffect(load, []);
    return (
        <>
            <div className="d-flex justify-content-end mb-3">
                <Button size="sm" variant="primary" onClick={()=>setShowForm(true)}>
                    <Plus size={14} className="me-1"/>New Request
                </Button>
            </div>
            {loading ? <div className="text-center py-4"><Spinner animation="border"/></div> : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-uppercase text-muted" style={{fontSize:10}}>
                                <th className="ps-4">Date</th><th>Requested Time</th>
                                <th>Reason</th><th>Status</th><th className="pe-4">Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.length===0 && <tr><td colSpan={5} className="text-center text-muted py-4">No regularization requests yet.</td></tr>}
                            {requests.map((r,i)=>{
                                const sb=STATUS_BADGE[r.status]||STATUS_BADGE.pending;
                                return <tr key={i}>
                                    <td className="ps-4 fw-medium">{fmtDate(r.date)}</td>
                                    <td>
                                        {r.requested_check_in&&<span className="text-success">IN: {r.requested_check_in}</span>}
                                        {r.requested_check_in&&r.requested_check_out&&' · '}
                                        {r.requested_check_out&&<span className="text-danger">OUT: {r.requested_check_out}</span>}
                                    </td>
                                    <td style={{maxWidth:200}}>{r.reason}</td>
                                    <td><Badge bg={sb.bg} text={sb.text} style={{fontSize:10}}>{sb.label}</Badge></td>
                                    <td className="pe-4 text-muted">{r.remarks||'—'}</td>
                                </tr>;
                            })}
                        </tbody>
                    </Table>
                </Card>
            )}
            {showForm && <RegularizeFormModal onClose={()=>setShowForm(false)} onDone={()=>{setShowForm(false);load();}}/>}
        </>
    );
};

const PendingTab = ({ status='pending' }) => {
    const [requests, setRequests] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [selected, setSelected] = useState(null);
    const load = () => {
        setLoading(true);
        axiosInstance.get('/attendance/regularize/pending',{params:{status}})
            .then(r=>setRequests(r.data.data||[])).catch(()=>{}).finally(()=>setLoading(false));
    };
    useEffect(load,[status]);
    return (
        <>
            <div className="d-flex justify-content-between mb-3">
                <span className="text-muted small">{requests.length} request{requests.length!==1?'s':''}</span>
                <Button size="sm" variant="outline-secondary" onClick={load}><RefreshCw size={13}/></Button>
            </div>
            {loading ? <div className="text-center py-4"><Spinner animation="border"/></div> : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-uppercase text-muted" style={{fontSize:10}}>
                                <th className="ps-4">Employee</th><th>Date</th><th>Current Time</th>
                                <th>Requested Time</th><th>Reason</th><th>Status</th>
                                {status==='pending'&&<th className="pe-4">Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {requests.length===0&&<tr><td colSpan={7} className="text-center text-muted py-4">No {status} requests.</td></tr>}
                            {requests.map((r,i)=>{
                                const sb=STATUS_BADGE[r.status]||STATUS_BADGE.pending;
                                return <tr key={i}>
                                    <td className="ps-4"><div className="fw-medium">{r.employee_name}</div><div className="text-muted" style={{fontSize:10}}>{r.department_name}</div></td>
                                    <td>{fmtDate(r.date)}</td>
                                    <td className="text-muted">{r.current_check_in||'—'} / {r.current_check_out||'—'}</td>
                                    <td>
                                        {r.requested_check_in&&<span className="text-success">{r.requested_check_in}</span>}
                                        {r.requested_check_in&&r.requested_check_out&&' / '}
                                        {r.requested_check_out&&<span className="text-danger">{r.requested_check_out}</span>}
                                    </td>
                                    <td style={{maxWidth:180}}>{r.reason}</td>
                                    <td><Badge bg={sb.bg} text={sb.text} style={{fontSize:10}}>{sb.label}</Badge></td>
                                    {status==='pending'&&<td className="pe-4">
                                        <Button size="sm" variant="outline-success" className="me-1 py-0" onClick={()=>setSelected({...r,action:'approve'})}><CheckCircle size={13}/></Button>
                                        <Button size="sm" variant="outline-danger" className="py-0" onClick={()=>setSelected({...r,action:'reject'})}><XCircle size={13}/></Button>
                                    </td>}
                                </tr>;
                            })}
                        </tbody>
                    </Table>
                </Card>
            )}
            {selected&&<ApprovalModal request={selected} onClose={()=>setSelected(null)} onDone={()=>{setSelected(null);load();}}/>}
        </>
    );
};

const RegularizeFormModal = ({ onClose, onDone }) => {
    const todayStr = new Date().toISOString().slice(0,10);
    const minDate  = (()=>{ const d=new Date(); d.setDate(d.getDate()-MAX_PAST_DAYS); return d.toISOString().slice(0,10); })();
    const [form,     setFormState] = useState({ date:'', requested_check_in:'', requested_check_out:'', reason:'' });
    const [errors,    setErrors]   = useState({});
    const [attempted, setAttempted]= useState(false);
    const [saving,    setSaving]   = useState(false);

    const setField = (k,v) => {
        setFormState(p=>({...p,[k]:v}));
        if(attempted) setErrors(prev=>{ const n={...prev}; delete n[k]; delete n.times; return n; });
    };

    const save = async () => {
        setAttempted(true);
        const errs = validateForm(form);
        if(Object.keys(errs).length>0){ setErrors(errs); return; }
        setSaving(true);
        try {
            await axiosInstance.post('/attendance/regularize', form);
            toast.success('Regularization request submitted!');
            onDone();
        } catch(err){ toast.error(err.response?.data?.message||'Failed to submit'); }
        finally{ setSaving(false); }
    };

    const reasonLen = form.reason?.trim().length||0;

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">Request Attendance Regularization</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info" className="small py-2">
                    Correct missed check-ins, forgotten check-outs, or wrong timestamps.
                    Corrections allowed up to <strong>{MAX_PAST_DAYS} days</strong> in the past.
                </Alert>
                <Row className="g-3">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Date *</Form.Label>
                        <Form.Control type="date" value={form.date} min={minDate} max={todayStr}
                            isInvalid={attempted&&!!errors.date}
                            onChange={e=>setField('date',e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.date}</Form.Control.Feedback>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Correct Check-in Time</Form.Label>
                        <Form.Control type="time" value={form.requested_check_in}
                            isInvalid={attempted&&!!errors.times}
                            onChange={e=>setField('requested_check_in',e.target.value)}/>
                    </Col>
                    <Col xs={6}>
                        <Form.Label className="small fw-bold">Correct Check-out Time</Form.Label>
                        <Form.Control type="time" value={form.requested_check_out}
                            isInvalid={attempted&&!!errors.times}
                            onChange={e=>setField('requested_check_out',e.target.value)}/>
                    </Col>
                    {attempted&&errors.times&&(
                        <Col xs={12} className="pt-0">
                            <div className="text-danger" style={{fontSize:12.5}}>{errors.times}</div>
                        </Col>
                    )}
                    <Col xs={12}>
                        <Form.Label className="small fw-bold d-flex justify-content-between">
                            <span>Reason *</span>
                            <span className={reasonLen>450?'text-danger':'text-muted'} style={{fontWeight:400,fontSize:11}}>
                                {reasonLen}/500
                            </span>
                        </Form.Label>
                        <Form.Control as="textarea" rows={3} maxLength={500}
                            placeholder="Explain why attendance needs correction (min 10 characters)…"
                            value={form.reason}
                            isInvalid={attempted&&!!errors.reason}
                            onChange={e=>setField('reason',e.target.value)}/>
                        <Form.Control.Feedback type="invalid">{errors.reason}</Form.Control.Feedback>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onClose}>Cancel</Button>
                <Button variant="primary" onClick={save} disabled={saving}>
                    {saving?<Spinner size="sm"/>:'Submit Request'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const ApprovalModal = ({ request, onClose, onDone }) => {
    const [remarks,    setRemarks]    = useState('');
    const [remarksErr, setRemarksErr] = useState('');
    const [attempted,  setAttempted]  = useState(false);
    const [saving,     setSaving]     = useState(false);
    const isApprove = request.action==='approve';

    const handleRemarks = (v) => {
        setRemarks(v);
        if(attempted) setRemarksErr('');
    };

    const submit = async () => {
        setAttempted(true);
        if(!isApprove && !remarks.trim()){
            setRemarksErr('Please provide a reason for rejection'); return;
        }
        if(!isApprove && remarks.trim().length<5){
            setRemarksErr('Rejection reason must be at least 5 characters'); return;
        }
        setSaving(true);
        try {
            await axiosInstance.patch(`/attendance/regularize/${request.id}`, {
                status:  isApprove?'approved':'rejected',
                remarks: remarks.trim()||undefined,
            });
            toast.success(`Request ${isApprove?'approved':'rejected'}`);
            onDone();
        } catch(err){ toast.error(err.response?.data?.message||'Action failed'); }
        finally{ setSaving(false); }
    };

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    {isApprove?'✅ Approve':'❌ Reject'} Regularization
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="p-3 rounded-3 mb-3" style={{background:'#f8fafc'}}>
                    <div className="fw-bold small">{request.employee_name}</div>
                    <div className="text-muted small">Date: {fmtDate(request.date)}</div>
                    <div className="text-muted small">Requested: {request.requested_check_in||'—'} → {request.requested_check_out||'—'}</div>
                    <div className="small mt-1">{request.reason}</div>
                </div>
                <Form.Label className="small fw-bold">
                    Remarks
                    {!isApprove&&<span className="text-danger ms-1">*</span>}
                    {isApprove&&<span className="text-muted fw-normal ms-1">(optional)</span>}
                </Form.Label>
                <Form.Control as="textarea" rows={2} maxLength={300}
                    placeholder={isApprove?'Optional note for the employee…':'Reason for rejection (required, min 5 characters)'}
                    value={remarks}
                    isInvalid={attempted&&!!remarksErr}
                    onChange={e=>handleRemarks(e.target.value)}/>
                {attempted&&remarksErr&&(
                    <div className="text-danger mt-1" style={{fontSize:12.5}}>{remarksErr}</div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onClose}>Cancel</Button>
                <Button variant={isApprove?'success':'danger'} onClick={submit} disabled={saving}>
                    {saving?<Spinner size="sm"/>:isApprove?'✅ Approve':'❌ Reject'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AttendanceRegularization;
